import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

// Student Loan Calculator — matches image: two sections
export default function StudentLoanCalc() {
  return (
    <CalcShell look="cnet" title="Student Loan Calculator" subtitle="Simple student loan calculator and repayment planner with extra payments."
      about={<>
        <p>Federal and private <b>student loans</b> are the primary way U.S. students finance higher education. Interest rates vary; federal loans usually have lower fixed rates than private ones.</p>
      </>}
    >
      <SimpleSection/>
      <hr className="my-8 border-slate-200" />
      <RepaymentSection/>
      <hr className="my-8 border-slate-200" />
      <ProjectionSection/>
    </CalcShell>
  );
}

function ProjectionSection() {
  const [semesters, setSemesters] = useState(8);
  const [perSem, setPerSem] = useState(5000);
  const [rate, setRate] = useState(6.8);
  const [gapYears, setGapYears] = useState(0.5); // grace period
  const c = useMemo(() => {
    // Loans accrue interest during school (unsubsidized). Simple monthly compounding.
    // For each semester (6 months apart), borrow amount then continue accruing.
    const rMon = rate/100/12;
    let bal = 0;
    const rows = [];
    for (let s = 0; s < semesters; s++) {
      bal += perSem;
      // Accrue 6 months of interest
      for (let m = 0; m < 6; m++) bal *= (1 + rMon);
      rows.push({ semester: s + 1, borrowed: perSem * (s + 1), balance: bal });
    }
    // Grace period accrual
    const graceMonths = Math.round(gapYears * 12);
    for (let m = 0; m < graceMonths; m++) bal *= (1 + rMon);
    const totalBorrowed = perSem * semesters;
    const accrued = bal - totalBorrowed;
    return { balance: bal, totalBorrowed, accrued, rows };
  }, [semesters, perSem, rate, gapYears]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Student Loan Projection Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">Estimate the total student loan balance after school, assuming unsubsidized interest accrues while enrolled and during the grace period.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Number of Semesters</label><div className="flex gap-2"><input type="number" value={semesters} onChange={e=>setSemesters(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">semesters</span></div></div>
          <div className="field"><label>Borrowed per Semester</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={perSem} onChange={e=>setPerSem(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Grace Period after Graduation</label><div className="flex gap-2"><input type="number" step="0.1" value={gapYears} onChange={e=>setGapYears(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Projected Balance at Repayment Start</div><div className="result-value" data-testid="sl-projection">{fmt(c.balance)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total Borrowed</td><td className="text-right">{fmt(c.totalBorrowed)}</td></tr>
            <tr><td>Interest Accrued</td><td className="text-right text-orange-600">{fmt(c.accrued)}</td></tr>
          </tbody></table>
        </div>
      </div>
      <div className="mt-4">
        <h4 className="font-semibold text-slate-900 mb-2">Semester-by-semester schedule</h4>
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-1.5 text-left">Semester</th><th className="px-3 py-1.5 text-right">Total borrowed</th><th className="px-3 py-1.5 text-right">Balance (with accrual)</th></tr></thead>
          <tbody>
            {c.rows.map((r,i) => (<tr key={i} className={i%2?'bg-slate-50':''}><td className="px-3 py-1.5">{r.semester}</td><td className="px-3 py-1.5 text-right">{fmt(r.borrowed)}</td><td className="px-3 py-1.5 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SimpleSection() {
  const [balance, setBalance] = useState(30000);
  const [term, setTerm] = useState(10);
  const [rate, setRate] = useState(6.8);
  const c = useMemo(() => {
    const r = rate/100/12; const n = term*12;
    const M = r === 0 ? balance/n : (balance*r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    return { M, total: M*n, interest: M*n - balance };
  }, [balance, term, rate]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Simple Student Loan Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">Enter your loan balance, remaining term and interest rate to see the monthly payment.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan Balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Remaining Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Repayment</div><div className="result-value" data-testid="sl-simple-payment">{fmt(c.M)}<span className="text-lg font-normal">/month</span></div></div>
          <table className="data-table"><tbody>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
            <tr><td>Total Payments</td><td className="text-right font-semibold">{fmt(c.total)}</td></tr>
          </tbody></table>
          <div className="mt-3"><PieChart parts={[{label:'Principal', value:balance, color:'#2563eb'},{label:'Interest', value:c.interest, color:'#10b981'}]} size={150}/></div>
        </div>
      </div>
    </div>
  );
}

function RepaymentSection() {
  const [balance, setBalance] = useState(30000);
  const [monthly, setMonthly] = useState(350);
  const [rate, setRate] = useState(6.8);
  const [option, setOption] = useState('extra'); // 'payoff' | 'extra' | 'normal'
  const [extraMonth, setExtraMonth] = useState(150);
  const [extraYear, setExtraYear] = useState(0);
  const [oneTime, setOneTime] = useState(0);

  const simulate = (extraM, extraY, oneT) => {
    let bal = balance;
    const r = rate/100/12;
    let months = 0, totalInt = 0, totalPay = 0;
    let cm = 0;
    while (bal > 0.01 && months < 1200) {
      months++; cm++;
      const interest = bal * r;
      let princ = monthly + extraM - interest;
      if (cm === 12) { princ += extraY; cm = 0; }
      if (months === 1) princ += oneT;
      if (princ + interest > bal + monthly) princ = bal;
      bal = Math.max(0, bal - princ);
      const pay = monthly + extraM + (cm===0?extraY:0) + (months===1?oneT:0);
      totalInt += interest;
      totalPay += Math.min(pay, bal + interest + princ);
    }
    return { months, totalInt, totalPay: monthly*months + extraM*months + extraY*Math.floor(months/12) + oneT };
  };
  const c = useMemo(() => {
    const orig = simulate(0, 0, 0);
    const extra = option === 'extra' ? simulate(extraMonth, extraYear, oneTime) : orig;
    const payoff = option === 'payoff' ? { months: 0, totalInt: 0, totalPay: balance } : null;
    const monthsToYM = m => `${Math.floor(m/12)} years and ${m%12} months`;
    return { orig, extra, payoff, monthsToYM };
  }, [balance, monthly, rate, option, extraMonth, extraYear, oneTime]);

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Student Loan Repayment Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">Compare your original schedule with extra payments or lump-sum payoff.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan Balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Monthly Payment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthly} onChange={e=>setMonthly(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">/month</span></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt" checked={option==='payoff'} onChange={()=>setOption('payoff')}/> Payoff altogether</label>
            <label className="flex items-start gap-2 text-sm"><input type="radio" name="opt" checked={option==='extra'} onChange={()=>setOption('extra')}/>
              <div className="flex-1">
                <div className="font-medium mb-2">Repayment with extra payments</div>
                <div className="grid grid-cols-2 gap-2 items-center text-xs">
                  <input type="number" value={extraMonth} onChange={e=>setExtraMonth(+e.target.value||0)} className="!py-1"/><span>per month</span>
                  <input type="number" value={extraYear} onChange={e=>setExtraYear(+e.target.value||0)} className="!py-1"/><span>per year</span>
                  <input type="number" value={oneTime} onChange={e=>setOneTime(+e.target.value||0)} className="!py-1"/><span>one time</span>
                </div>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="opt" checked={option==='normal'} onChange={()=>setOption('normal')}/> Normal repayment</label>
          </div>
        </div>
        <div>
          {option === 'payoff' && (
            <div className="result-panel mb-4"><div className="result-title">Payoff Altogether</div><div className="result-value">{fmt(balance)}</div></div>
          )}
          {option === 'extra' && (
            <>
              <div className="result-panel mb-3"><div className="result-title">Pay off in</div><div className="result-value" data-testid="sl-extra-time">{c.monthsToYM(c.extra.months)}</div></div>
              <table className="data-table mb-3"><tbody>
                <tr><td>Total Payments</td><td className="text-right font-semibold">{fmt(c.extra.totalPay)}</td></tr>
                <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.extra.totalInt)}</td></tr>
              </tbody></table>
            </>
          )}
          <div className="text-sm font-semibold text-slate-700 mb-2">The Original Payoff Schedule</div>
          <table className="data-table"><tbody>
            <tr><td>Remaining Term</td><td className="text-right">{c.monthsToYM(c.orig.months)}</td></tr>
            <tr><td>Total Payments</td><td className="text-right">{fmt(c.orig.totalPay)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right">{fmt(c.orig.totalInt)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}
